import cloneDeep from 'lodash/cloneDeep';
import type { Container } from 'pixi.js';
import { Ticker } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { LayoutAnimationData, LayoutAnimationSetterBlock, LayoutAnimationTweenBlock } from './types';
import { removeElement } from '../../../utils/array-utils';
import type { Tween } from '../../TweenManager/Tween';
import { adaptTarget } from './adaptTarget';
import { layoutEasingToGameEasingMap } from './layoutEasingToGameEasingMap';

type AnimationCallbackConfig = Record<string, (name: string, container: Container) => void>;

export class LayoutAnimationPlayer {
  public readonly onComplete = new Signal();

  private readonly initialData: LayoutAnimationData;

  private readonly ticker = new Ticker();

  private readonly activeTweens = new Set<Tween>();

  private animationData: LayoutAnimationData;

  private currentTime = 0;

  private destroyed = false;

  private loop = false;

  private paused = false;

  private shouldPauseAfterLoop = false;

  private readonly callbackPrefixes: string[] | null;

  constructor(
    private readonly layoutInstance: Record<string, object>,
    animationData: LayoutAnimationData,
    private readonly callbacks: AnimationCallbackConfig | null = null,
  ) {
    this.initialData = cloneDeep(animationData);

    this.callbackPrefixes = this.callbacks ? Object.keys(this.callbacks) : null;

    this.animationData = cloneDeep(animationData);

    this.ticker.add(() => {
      this.update(this.ticker.elapsedMS);
    });

    (layoutInstance.root as Container).on('destroyed', () => {
      this.loop = false;
      this.destroy();
    });

    if ((layoutInstance.root as Container).destroyed) {
      this.destroy();
    }
  }

  public play(loop: boolean): void {
    this.paused = false;
    this.shouldPauseAfterLoop = false;
    this.loop = loop;
    this.update(0);
    this.ticker.start();
  }

  public setPause(paused: boolean): this {
    this.paused = paused;
    this.activeTweens.forEach((tween) => (paused ? tween.pause() : tween.resume()));
    return this;
  }

  public pauseOnLoop(): void {
    this.shouldPauseAfterLoop = true;
  }

  private update(passedMs: number): void {
    if (this.paused) return;
    if (this.destroyed) return;

    this.currentTime += passedMs * this.animationData.playingSpeed;

    const blocksToStart = this.animationData.blocks.filter((block) => this.currentTime >= block.startAt);

    blocksToStart.forEach((block) => {
      this.checkBlockCallbacks(block);
      removeElement(this.animationData.blocks, block);
    });

    this.executeBlocks(blocksToStart);

    if (this.currentTime >= this.animationData.totalDuration && this.activeTweens.size === 0) {
      if (this.loop) {
        this.resetAnimation();
        if (this.shouldPauseAfterLoop) {
          this.shouldPauseAfterLoop = false;
          this.setPause(true);
        }
        return;
      }

      this.destroy();
      this.onComplete.emit();
    }
  }

  private checkBlockCallbacks(block: LayoutAnimationTweenBlock | LayoutAnimationSetterBlock): void {
    if (!this.callbacks || !this.callbackPrefixes) return;

    const usedPrefix = this.callbackPrefixes.find((prefix) => block.name.startsWith(prefix));
    if (!usedPrefix) return;

    this.callbacks[usedPrefix]?.(
      block.name.replace(usedPrefix, ''),
      this.layoutInstance[block.sourceName] as Container,
    );
  }

  private executeBlocks(blocks: (LayoutAnimationSetterBlock | LayoutAnimationTweenBlock)[]): void {
    blocks
      .sort((a, b) => Number(b.type === 'Setter') - Number(a.type === 'Setter'))
      .forEach((block) => {
        this.executeBlock(block);
      });
  }

  private executeBlock(block: LayoutAnimationTweenBlock | LayoutAnimationSetterBlock): void {
    if (block.type === 'Setter') {
      this.executeSetterBlock(block);
    } else {
      this.executeTweenBlock(block);
    }
  }

  private executeTweenBlock(block: LayoutAnimationTweenBlock): void {
    let target = this.layoutInstance[block.sourceName] as Container;
    if (!target) return;
    if (target.destroyed) return;

    // nested layout
    if (!target.tweenTo) {
      target = (target as unknown as { root: Container }).root;
    }

    const tween = target.tweenTo(
      adaptTarget(block.target),
      block.duration,
      layoutEasingToGameEasingMap[block.easing],
      true,
    );

    if (!tween) return;

    this.activeTweens.add(tween);
    tween.onComplete.add(() => {
      this.activeTweens.delete(tween);
    });
  }

  private executeSetterBlock(block: LayoutAnimationSetterBlock): void {
    const target = this.layoutInstance[block.sourceName] as Container;
    if (!target) return;
    if (target.destroyed) return;

    // in case of nested layout
    if (!target.props) {
      (target as unknown as { root: Container }).root.props(adaptTarget(block.target));
    } else {
      target.props(adaptTarget(block.target));
    }
  }

  private resetAnimation(): void {
    this.currentTime = 0;
    this.animationData = cloneDeep(this.initialData);
  }

  public destroy(): void {
    if (this.destroyed) return;

    this.activeTweens.forEach((tween) => tween.stop(true));

    this.ticker.destroy();
    this.destroyed = true;
  }
}
